import ResourceTyped from 'Resources/ResourceTyped';
import { normaliseApproval, type AgentApproval } from 'Resources/AgentApprovals';

type Conversation = {
	id: number;
	title: string;
	starred: boolean;
	contextType: string;
	contextId?: number;
	parentConversationId?: number | null;
	regDate: string;
	modDate: string;
	messages?: AssistantMessage[];
};

/** A filter as the catalogue endpoint serves it — the registry, resolved for a market. */
export type CatalogueFilter = {
	key: string;
	group: 'company' | 'location' | 'financials' | 'crm' | 'contacts' | 'signals';
	labelKey: string;
	source: string;
	valueType: string;
	pro: boolean;
	currency: boolean;
	lookup: boolean;
	/** The index column for this market — what a lookup filter's values are fetched by. */
	column?: string;
	/** Set only when the filter cannot be used at all — the row renders disabled. */
	unavailableReason?: 'needsNewSource' | 'postEnrichmentOnly' | 'monitoredOnly';
};

export type ProspectingSessionState = {
	conversationId: number;
	country: string;
	entityMode: string;
	filters: { key: string; value: unknown; setBy?: string; reasoning?: string }[];
};

type AssistantMessage = {
	id: number;
	role: 'user' | 'assistant' | 'tool';
	content: string;
	metadata?: Record<string, unknown>;
	regDate: string;
};

/**
 * WEB-6799 — inbox items. Two kinds today: `approval` (a pending approval
 * request targeted at the user) and `unread` (a conversation with unseen agent
 * activity). Future iterations may add delegations as new
 * kinds. Consumers must switch on `kind` rather than assume approval-shape
 * fields exist.
 */
/**
 * What a `permissionUpgrade` approval is asking to be allowed to do unattended.
 * Absent on every other action.
 */
export type InboxUpgradeRequest =
	| { type: 'entity'; entity: string; action: string }
	| { type: 'capability'; capability: string };

export type InboxApprovalItem = {
	id: number;
	kind: 'approval';
	action: string;
	/**
	 * Null for a `permissionUpgrade`: it's raised by the upgrade tracker rather
	 * than by a run, so it belongs to no conversation and is resolved inline in
	 * the Inbox instead of by opening a thread.
	 */
	conversationId: number | null;
	/** Conversation title, or — for an upgrade — the name of the agent asking. */
	title: string | null;
	runId: number;
	agentId: number;
	regDate: string;
	expiresAt?: string;
	upgrade?: InboxUpgradeRequest | null;
	/**
	 * WEB-11464: the row itself, so the Inbox can render the same proposal card
	 * the conversation renders and take the decision in place. Absent on a
	 * `permissionUpgrade`, which has its own inline Allow/Deny controls.
	 */
	approval?: AgentApproval | null;
};

export type InboxConversationItem = {
	id: number;
	/** `unread` = unseen agent activity; `earlier` = already seen (shown dimmed). */
	kind: 'unread' | 'earlier';
	conversationId: number;
	title: string | null;
	/** Conversation context type — drives the row's tag (Routine vs Message). */
	contextType: string | null;
	/** The latest agent message, used for the one-line preview. */
	lastMessage: string | null;
	regDate: string;
};

/**
 * A Playbook whose latest run failed (WEB-7821) — an ambient "needs attention"
 * signal, not a chat message. Clears automatically once the next run succeeds.
 */
export type InboxFailedRunItem = {
	id: string;
	kind: 'failedRun';
	runId: number;
	workflowId: number;
	conversationId: number | null;
	/** The bound routine's label, or null (UI falls back to a generic title). */
	title: string | null;
	error: string | null;
	regDate: string | null;
};

export type InboxItem = InboxApprovalItem | InboxConversationItem | InboxFailedRunItem;

// Chat resource — conversations, streaming send, inbox, approval execution
// and the personal-assistant profile. The last piece of the Assistant god
// resource split (WEB-7958); the other domains live in AssistantRoutines,
// AssistantWorkflows, AssistantUsage and AssistantCompanyProfile.
class AssistantChat extends ResourceTyped<Conversation> {
	constructor() {
		super('assistant/conversations');
	}

	createConversation(contextType = 'general', contextId?: number): Promise<{ id: number }> {
		return this._postRequest('', { contextType, contextId }).then(res => res.data?.data ?? res.data);
	}

	getConversation(id: number): Promise<Conversation> {
		return this._getRequest(String(id)).then(res => res.data?.data ?? res.data);
	}

	// WEB-7348: resolve the shared authoring conversation for a page. Visibility
	// follows page access, so every admin opening the page gets the same thread
	// (not just its creator). Resolves to null when the page has no conversation
	// yet — none is created until the first message is sent.
	getPageConversation(pageId: number): Promise<Conversation | null> {
		return fetch(`/api/v2/assistant/pages/${pageId}/conversation`, {
			headers: { 'Content-Type': 'application/json' }
		})
			.then(res => res.json())
			.then(res => res?.data ?? null);
	}

	deleteConversation(id: number): Promise<void> {
		return this._deleteRequest(String(id)).then(() => undefined);
	}

	renameConversation(id: number, title: string): Promise<void> {
		return this._putRequest(String(id), { title }).then(() => undefined);
	}

	starConversation(id: number, starred: boolean): Promise<void> {
		return fetch(`/api/v2/assistant/conversations/${id}/star`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ starred })
		}).then(() => undefined);
	}

	updatePostMeetingMessage(
		conversationId: number,
		messageId: number,
		updates: { content?: string; title?: string }
	): Promise<void> {
		return this._putRequest(`${conversationId}/post-meeting-message/${messageId}`, updates).then(() => undefined);
	}

	/** `agentId` narrows it to the user's chats with that agent (up to 100) instead of their 20 newest overall. */
	listConversations(agentId?: number): Promise<Conversation[]> {
		return this._getRequest('', agentId ? { params: { agentId } } : undefined).then(
			res => res.data?.data ?? res.data
		);
	}

	/**
	 * The prospecting workspace's sessions, newest first (WEB-9403). A session is
	 * a conversation plus its filters, so the user's `prospecting` conversations
	 * are the session list. Creating one goes through `createConversation`.
	 */
	listProspectingConversations(): Promise<Conversation[]> {
		return fetch('/api/v2/assistant/prospecting/conversations', {
			headers: { 'Content-Type': 'application/json' }
		})
			.then(res => res.json())
			.then(res => res?.data ?? []);
	}

	/**
	 * What the filter catalogue can offer in one market (WEB-9556). Read from the
	 * shared registry in base/, which the browser cannot import - the market
	 * decides the contents, so this is refetched when country changes.
	 */
	getProspectingFilterCatalogue(country: string): Promise<CatalogueFilter[]> {
		return fetch(`/api/v2/assistant/prospecting/filterCatalogue?country=${encodeURIComponent(country)}`, {
			headers: { 'Content-Type': 'application/json' }
		})
			.then(res => res.json())
			.then(res => res?.data ?? []);
	}

	/** A session's filters and market. Null until the first filter is set. */
	getProspectingSession(conversationId: number): Promise<ProspectingSessionState | null> {
		return fetch(`/api/v2/assistant/prospecting/conversations/${conversationId}/session`, {
			headers: { 'Content-Type': 'application/json' }
		})
			.then(res => res.json())
			.then(res => res?.data ?? null);
	}

	/**
	 * A rep's own edit. `filter.value: null` clears it. Returns the session as
	 * stored, plus the keys the market could not answer and so were dropped -
	 * which is how a country change reports what it cost.
	 */
	updateProspectingSession(
		conversationId: number,
		edit: { country?: string; entityMode?: string; filter?: { key: string; value: unknown } }
	): Promise<ProspectingSessionState & { dropped: string[] }> {
		return fetch(`/api/v2/assistant/prospecting/conversations/${conversationId}/session`, {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(edit)
		})
			.then(res => res.json())
			.then(res => {
				// A failed edit answers `{ error, data: null }`, and the pane reads
				// `country` and `filters` straight off this. Returning that shape made
				// the caller throw "Cannot read properties of undefined (reading
				// 'country')" — a message that says nothing about what failed. Throw
				// the server's reason so notifyAIWorkspaceError reports it.
				if (!res?.data) {
					throw new Error(res?.error?.msg || 'Could not update the prospecting session');
				}
				return res.data;
			});
	}

	/**
	 * Headless send — dispatch the agent run to the worker instead of streaming
	 * inline over SSE. Returns immediately; the run's output arrives over Pusher
	 * (`agent.runStarted` / `runEvent` / `runFinalized`), watched via
	 * `useAgentRunEvents`. Used by page authoring, where generation can outrun
	 * the HTTP request timeout.
	 */
	async sendMessageHeadless(
		conversationId: number,
		message: string,
		files?: File[],
		regenerate?: boolean
	): Promise<{ conversationId: number }> {
		const url = `/api/v2/assistant/conversations/${conversationId}/messages/async`;
		let body: BodyInit;
		const headers: Record<string, string> = {};
		// Mirror the SSE send: multipart when files are attached (the API persists
		// them as context_items for the worker to rebuild), JSON otherwise.
		// Regenerate never carries files, so it always takes the JSON branch — the
		// flag re-runs the existing turn instead of posting a new user message
		// (WEB-7801).
		if (files && files.length > 0) {
			const formData = new FormData();
			formData.append('message', message);
			for (const file of files) {
				formData.append('files', file);
			}
			body = formData;
		} else {
			headers['Content-Type'] = 'application/json';
			body = JSON.stringify({ message, ...(regenerate ? { regenerate: true } : {}) });
		}

		const response = await fetch(url, { method: 'POST', headers, body });
		if (!response.ok) {
			throw new Error('Failed to send message');
		}
		const json = await response.json();
		return json.data ?? json;
	}

	async sendMessageStream(
		conversationId: number,
		message: string,
		contextType: string = 'general',
		contextId?: number,
		options: {
			files?: File[];
			isScheduledRun?: boolean;
			runId?: string;
			runTimestamp?: string;
			regenerate?: boolean;
			turnContext?: string;
			trustedContext?: string;
			signal?: AbortSignal;
			onText?: (text: string) => void;
			/** Everything streamed via `onText` for this turn is void — the agent
			 *  abandoned that round and is replacing it (WEB-9264). */
			onTextReset?: () => void;
			onDone?: () => void;
			onError?: (error: string) => void;
			onPanel?: (panel: unknown) => void;
			onImages?: (urls: string[]) => void;
			onAttachments?: (
				attachments: Array<{ name: string; mimeType: string; sizeBytes?: number; url?: string }>
			) => void;
			onDocument?: (attachment: { name: string; mimeType: string; sizeBytes?: number; url?: string }) => void;
			onStatus?: (status: string) => void;
			onMultiAction?: (config: unknown) => void;
			onContactScrape?: (config: unknown) => void;
			onWriteProposal?: (proposal: {
				entity: string;
				action: string;
				data: Record<string, unknown>;
				approvalId?: number | null;
				matchingContacts?: number;
			}) => void;
			onEmailDraft?: (draft: {
				contactIds: number[];
				subject: string;
				body: string;
				ccContactIds?: number[];
				clientId?: number;
				attachments?: { id: number; name: string }[];
				approvalId?: number | null;
			}) => void;
			onOfferDraft?: (draft: {
				clientId: number;
				contactId: number;
				products: {
					productId: number;
					productName?: string;
					quantity: number;
					unitPrice?: number;
					discount?: number;
				}[];
				currency: string;
				validUntil: string;
				description?: string;
				notes?: string;
				approvalId?: number | null;
			}) => void;
			/** A drafted prospecting add (WEB-11807). */
			onProspectingAdd?: (draft: {
				action: string;
				companyName?: string;
				orgNumber?: string;
				contacts: { name: string; role?: string }[];
				approvalId: number;
			}) => void;
			onAgentCreated?: (info: { agentId: number; name: string; updated?: boolean }) => void;
			onIdentityUpdated?: (identity: { name?: string; role?: string; avatarUrl?: string | null }) => void;
			onThinking?: (snapshot: string) => void;
			onSuggestedReplies?: (replies: string[]) => void;
			onDelegation?: (delegation: {
				conversationId: number;
				agentId: number;
				agentName?: string;
				title?: string;
				rationale?: string;
				status: string;
				isSelf?: boolean;
			}) => void;
		} = {}
	): Promise<void> {
		const {
			files,
			isScheduledRun,
			runId,
			runTimestamp,
			regenerate,
			turnContext,
			trustedContext,
			signal,
			onText,
			onTextReset,
			onDone,
			onError,
			onPanel,
			onImages,
			onAttachments,
			onDocument,
			onStatus,
			onMultiAction,
			onContactScrape,
			onWriteProposal,
			onEmailDraft,
			onOfferDraft,
			onProspectingAdd,
			onAgentCreated,
			onIdentityUpdated,
			onThinking,
			onSuggestedReplies,
			onDelegation
		} = options;
		let body: BodyInit;
		const headers: Record<string, string> = {};

		if (files && files.length > 0) {
			const formData = new FormData();
			formData.append('message', message);
			formData.append('contextType', contextType);
			if (contextId !== undefined) formData.append('contextId', String(contextId));
			if (turnContext) formData.append('turnContext', turnContext);
			if (trustedContext) formData.append('trustedContext', trustedContext);
			for (const file of files) {
				formData.append('files', file);
			}
			body = formData;
		} else {
			headers['Content-Type'] = 'application/json';
			body = JSON.stringify({
				message,
				contextType,
				contextId,
				...(isScheduledRun && { isScheduledRun: true }),
				...(runId && { runId }),
				...(runTimestamp && { runTimestamp }),
				...(regenerate && { regenerate: true }),
				...(turnContext && { turnContext }),
				...(trustedContext && { trustedContext })
			});
		}

		let response: Response;
		try {
			response = await fetch(`/api/v2/assistant/conversations/${conversationId}/messages`, {
				method: 'POST',
				headers,
				body,
				signal
			});
		} catch (err) {
			// Silent exit on user-triggered abort — the caller is responsible for
			// cleaning up UI state. Anything else surfaces as an error.
			if (err instanceof DOMException && err.name === 'AbortError') return;
			onError?.('Failed to send message');
			return;
		}

		if (!response.ok || !response.body) {
			onError?.('Failed to send message');
			return;
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = '';

		let gotDone = false;
		let gotError = false;
		let streamDone = false;

		while (!streamDone) {
			let readResult: ReadableStreamReadResult<Uint8Array>;
			try {
				readResult = await reader.read();
			} catch (err) {
				// Abort during read — stop cleanly without firing onError.
				if (err instanceof DOMException && err.name === 'AbortError') return;
				throw err;
			}
			const { done, value } = readResult;
			if (done) {
				streamDone = true;
				break;
			}

			buffer += decoder.decode(value, { stream: true });
			const lines = buffer.split('\n');
			buffer = lines.pop() ?? '';

			for (const line of lines) {
				if (line.startsWith('data: ')) {
					try {
						const data = JSON.parse(line.slice(6));
						if (data.type === 'text' && data.text) {
							onText?.(data.text);
						} else if (data.type === 'text_reset') {
							onTextReset?.();
						} else if (data.type === 'panel' && data.panel) {
							onPanel?.(data.panel);
						} else if (data.type === 'images' && data.urls) {
							onImages?.(data.urls);
						} else if (data.type === 'attachments' && Array.isArray(data.attachments)) {
							onAttachments?.(data.attachments);
						} else if (data.type === 'document' && data.attachment) {
							onDocument?.(data.attachment);
						} else if (data.type === 'status' && data.text) {
							onStatus?.(data.text);
						} else if (data.type === 'multi_action' && data.config) {
							onMultiAction?.(data.config);
						} else if (data.type === 'contact_scrape' && data.config) {
							onContactScrape?.(data.config);
						} else if (data.type === 'write_proposal' && data.proposal) {
							onWriteProposal?.(data.proposal);
						} else if (data.type === 'email_draft' && data.emailDraft) {
							onEmailDraft?.(data.emailDraft);
						} else if (data.type === 'offer_draft' && data.offerDraft) {
							onOfferDraft?.(data.offerDraft);
						} else if (data.type === 'prospecting_add' && data.prospectingAdd) {
							onProspectingAdd?.(data.prospectingAdd);
						} else if (data.type === 'agent_created' && data.agentCreated) {
							onAgentCreated?.(data.agentCreated);
						} else if (data.type === 'identity_updated' && data.identity) {
							onIdentityUpdated?.(data.identity);
						} else if (data.type === 'thinking' && data.text) {
							onThinking?.(data.text);
						} else if (data.type === 'suggested_replies' && Array.isArray(data.suggestedReplies)) {
							onSuggestedReplies?.(
								data.suggestedReplies.filter((r: unknown): r is string => typeof r === 'string')
							);
						} else if (data.type === 'delegation' && data.delegation) {
							onDelegation?.(data.delegation);
						} else if (data.type === 'done') {
							gotDone = true;
							onDone?.();
						} else if (data.type === 'error') {
							gotError = true;
							onError?.(data.error);
						}
					} catch {
						// Skip malformed events
					}
				}
			}
		}

		// Process any remaining data in the buffer
		if (buffer.trim().startsWith('data: ')) {
			try {
				const data = JSON.parse(buffer.trim().slice(6));
				if (data.type === 'text' && data.text) {
					onText?.(data.text);
				} else if (data.type === 'done') {
					gotDone = true;
					onDone?.();
				}
			} catch {
				// Skip malformed
			}
		}

		// If stream ended without a done event, call onDone anyway (unless error already handled it)
		if (!gotDone && !gotError) {
			onDone?.();
		}
	}

	/**
	 * WEB-6799 — fetch the user's Inbox. v1 = pending approval requests
	 * targeted at the caller, oldest first. Backend filters; client just
	 * renders.
	 */
	getInbox({ background = false }: { background?: boolean } = {}): Promise<InboxItem[]> {
		// No retries: this is polled every 60s, so the shared axiosRetry default would turn an
		// API outage into 4 requests per tick per tab, and the caller discards late results anyway.
		return this._getRequest('', {
			methodName: 'getInbox',
			_url: 'assistant/inbox',
			params: background ? { noSessionRefresh: 1 } : undefined,
			'axios-retry': { retries: 0 },
			expectedStatuses: [403]
		})
			// The payload columns come back as JSON strings, the same as on the
			// single-approval endpoint — parse them here so the card adapters get
			// real objects (WEB-11464).
			.then(res =>
				((res.data?.data ?? []) as InboxItem[]).map(item =>
					item.kind === 'approval' && item.approval
						? { ...item, approval: normaliseApproval(item.approval) }
						: item
				)
			)
			.catch(err => {
				// 403 (workspaceAccessGuard) is an answer, not a failure — it arrives as normal
				// JSON with data:null, which every caller has always rendered as an empty inbox.
				// Rejecting would toast at all three call sites instead.
				if (err?.response?.status === 403) return [];
				throw err;
			});
	}

	/** WEB-7943 — clear a single conversation from the Inbox unread feed. */
	async markInboxRead(conversationId: number): Promise<void> {
		const res = await fetch(`/api/v2/assistant/inbox/${conversationId}/read`, { method: 'PUT' });
		if (!res.ok) throw new Error(`Failed to mark conversation as read (${res.status})`);
	}

	/** WEB-7943 — clear the given unread conversations (the rows currently shown). */
	async markAllInboxRead(conversationIds: number[]): Promise<void> {
		const res = await fetch('/api/v2/assistant/inbox/read-all', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ conversationIds })
		});
		if (!res.ok) throw new Error(`Failed to mark inbox as read (${res.status})`);
	}

	getProfile(): Promise<{ name: string; title?: string; avatarUrl: string | null; conversationCount: number }> {
		return fetch('/api/v2/assistant/profile')
			.then(res => res.json())
			.then(res => res.data);
	}

	updateProfile(updates: { name?: string; title?: string; avatarUrl?: string | null }): Promise<void> {
		return fetch('/api/v2/assistant/profile', {
			method: 'PUT',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates)
		}).then(() => undefined);
	}

	generateAvatar(): Promise<{ avatarUrl: string }> {
		return fetch('/api/v2/assistant/avatar/generate', { method: 'POST' })
			.then(res => res.json())
			.then(res => res.data);
	}

	submitFeedback(params: {
		type: 'up' | 'down';
		reason: string;
		assistantMessage?: string;
		userMessage?: string;
		conversationId?: number;
	}): Promise<{ saved: boolean; savedValue?: string; ignored: boolean; ignoreReason?: string }> {
		return fetch('/api/v2/assistant/feedback', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(params)
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	executeWrite(entity: string, action: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
		return fetch('/api/v2/assistant/write', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ entity, action, data })
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	executeSendEmail(
		contactIds: number[],
		subject: string,
		body: string,
		ccContactIds?: number[],
		clientId?: number
	): Promise<Record<string, unknown>> {
		return fetch('/api/v2/assistant/send-email', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ contactIds, subject, body, ccContactIds, clientId })
		})
			.then(res => res.json())
			.then(res => res.data);
	}

	reset(): Promise<void> {
		return fetch('/api/v2/assistant/reset', { method: 'POST' }).then(() => undefined);
	}
}

const resource = new AssistantChat();

export default resource;
